/**
 * RTWO Consensus Engine — Domain Models
 * Phase 4 | Version: 1.0 Consensus Edition
 *
 * Strongly typed domain models for the RTWO consensus layer.
 * Defines the internal consensus architecture and state machine
 * independent of networking, live validators, and mainnet deployment.
 *
 * Truthful Data Policy: All runtime values are null until real
 * infrastructure is connected. No fake data is permitted.
 */

// ─────────────────────────────────────────────
// CONSENSUS PRIMITIVES
// ─────────────────────────────────────────────

export type ConsensusHash = string;
export type ConsensusAddress = string;
export type ConsensusValidatorId = string;
export type ConsensusBlockHeight = bigint | null;
export type ConsensusTimestamp = number | null;
export type ConsensusSignature = string;
export type ConsensusEpochNumber = number | null;
export type ConsensusRound = number | null;
export type ConsensusVotingPower = bigint | null;
export type ConsensusStakeAmount = string; // string to preserve precision

export const CONSENSUS_ENGINE_VERSION = {
  version: '1.0.0-phaseB4',
  phase: 'Phase B.4 — Consensus P2P Runtime Activation',
  status: 'P2P Provider Connected — Network and Validator Set Pending',
} as const;

// ─────────────────────────────────────────────
// CONSENSUS PHASE DEFINITIONS
// ─────────────────────────────────────────────

/**
 * Consensus Phase Lifecycle:
 * Idle → Proposer Selection → Block Proposal → Vote Collection →
 * Vote Validation → Finality Check → Finalized → Failed
 */
export type ConsensusPhase =
  | 'idle'
  | 'proposer_selection'
  | 'block_proposal'
  | 'vote_collection'
  | 'vote_validation'
  | 'finality_check'
  | 'finalized'
  | 'failed';

export interface ConsensusPhaseDescriptor {
  phase: ConsensusPhase;
  label: string;
  description: string;
  terminal: boolean;
  requiresNetwork: boolean;
}

export const CONSENSUS_PHASES: ConsensusPhaseDescriptor[] = [
  {
    phase: 'idle',
    label: 'Idle',
    description: 'Consensus engine is initialized and waiting for the next block height to begin.',
    terminal: false,
    requiresNetwork: false,
  },
  {
    phase: 'proposer_selection',
    label: 'Proposer Selection',
    description:
      'A proposer validator is selected from the active validator set for the current round.',
    terminal: false,
    requiresNetwork: true,
  },
  {
    phase: 'block_proposal',
    label: 'Block Proposal',
    description:
      'The selected proposer constructs and broadcasts a block proposal to the validator set.',
    terminal: false,
    requiresNetwork: true,
  },
  {
    phase: 'vote_collection',
    label: 'Vote Collection',
    description: 'Validators cast prevote and precommit votes for the proposed block.',
    terminal: false,
    requiresNetwork: true,
  },
  {
    phase: 'vote_validation',
    label: 'Vote Validation',
    description: 'Collected votes are verified for signature validity and voting power thresholds.',
    terminal: false,
    requiresNetwork: true,
  },
  {
    phase: 'finality_check',
    label: 'Finality Check',
    description:
      'Quorum is evaluated. If 2/3+ voting power agrees, the block proceeds to finalization.',
    terminal: false,
    requiresNetwork: true,
  },
  {
    phase: 'finalized',
    label: 'Finalized',
    description:
      'Block has achieved BFT finality and is irreversibly committed to the canonical chain.',
    terminal: true,
    requiresNetwork: true,
  },
  {
    phase: 'failed',
    label: 'Failed',
    description:
      'Consensus round failed due to timeout, insufficient votes, or validator misbehavior.',
    terminal: true,
    requiresNetwork: true,
  },
];

// ─────────────────────────────────────────────
// VALIDATOR STATUS DEFINITIONS
// ─────────────────────────────────────────────

export type ValidatorCandidateStatus =
  'candidate' | 'active' | 'inactive' | 'jailed' | 'slashed' | 'removed';

export interface ValidatorStatusDescriptor {
  status: ValidatorCandidateStatus;
  label: string;
  description: string;
  canPropose: boolean;
  canVote: boolean;
}

export const VALIDATOR_STATUSES: ValidatorStatusDescriptor[] = [
  {
    status: 'candidate',
    label: 'Candidate',
    description: 'Validator has registered but has not yet been admitted to the active set.',
    canPropose: false,
    canVote: false,
  },
  {
    status: 'active',
    label: 'Active',
    description: 'Validator is in the active set and participates in consensus rounds.',
    canPropose: true,
    canVote: true,
  },
  {
    status: 'inactive',
    label: 'Inactive',
    description: 'Validator is registered but not currently participating in consensus.',
    canPropose: false,
    canVote: false,
  },
  {
    status: 'jailed',
    label: 'Jailed',
    description: 'Validator has been jailed due to downtime or protocol violations.',
    canPropose: false,
    canVote: false,
  },
  {
    status: 'slashed',
    label: 'Slashed',
    description: 'Validator has been penalized for double-signing or equivocation.',
    canPropose: false,
    canVote: false,
  },
  {
    status: 'removed',
    label: 'Removed',
    description: 'Validator has been permanently removed from the validator registry.',
    canPropose: false,
    canVote: false,
  },
];

// ─────────────────────────────────────────────
// CONSENSUS ENGINE STATUS
// ─────────────────────────────────────────────

export type ConsensusEngineStatus =
  'not_initialized' | 'initializing' | 'ready' | 'running' | 'paused' | 'error' | 'shutdown';

// ─────────────────────────────────────────────
// VOTE TYPE
// ─────────────────────────────────────────────

export type VoteType = 'prevote' | 'precommit' | 'nil_vote';

export type VoteStatus = 'pending' | 'valid' | 'invalid' | 'expired';

// ─────────────────────────────────────────────
// EPOCH STATUS
// ─────────────────────────────────────────────

export type EpochStatus = 'pending' | 'active' | 'finalizing' | 'finalized' | 'failed';

// ─────────────────────────────────────────────
// FINALITY STATUS
// ─────────────────────────────────────────────

export type FinalityStatus =
  'not_finalized' | 'pending_votes' | 'quorum_reached' | 'finalized' | 'failed';

// ─────────────────────────────────────────────
// SLASHING RULE TYPES
// ─────────────────────────────────────────────

export type SlashingSeverity = 'low' | 'medium' | 'high' | 'critical';

export type SlashingPenaltyType = 'stake_reduction' | 'jail' | 'tombstone' | 'removal';

export type SlashingRuleStatus = 'enabled' | 'disabled' | 'draft';

// ─────────────────────────────────────────────
// DOMAIN MODEL 1: RTWOConsensusState
// ─────────────────────────────────────────────

export interface RTWOConsensusState {
  /** Current block height being processed by consensus */
  currentHeight: ConsensusBlockHeight;

  /** Current consensus round within the current height */
  currentRound: ConsensusRound;

  /** Current epoch number */
  currentEpoch: ConsensusEpochNumber;

  /** Current phase of the consensus lifecycle */
  phase: ConsensusPhase;

  /** Validator ID of the current round proposer (null until selected) */
  proposer: ConsensusValidatorId | null;

  /** Hash of the current validator set */
  validatorSetHash: ConsensusHash | null;

  /** Hash of the block that has been locked in this round */
  lockedBlockHash: ConsensusHash | null;

  /** Hash of the most recently finalized block */
  finalizedBlockHash: ConsensusHash | null;

  /** Operational status of the consensus engine */
  status: ConsensusEngineStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 2: RTWOValidatorCandidate
// ─────────────────────────────────────────────

export interface RTWOValidatorCandidate {
  /** Unique identifier for this validator */
  validatorId: ConsensusValidatorId | null;

  /** On-chain operator address */
  operatorAddress: ConsensusAddress | null;

  /** BLS or Ed25519 public key for consensus signing */
  publicKey: string | null;

  /** Current status in the validator lifecycle */
  status: ValidatorCandidateStatus;

  /** Voting power assigned to this validator (proportional to stake) */
  votingPower: ConsensusVotingPower;

  /** Total stake amount backing this validator */
  stakeAmount: ConsensusStakeAmount | null;

  /** Timestamp when this validator joined the registry */
  joinedAt: ConsensusTimestamp;

  /** Timestamp of the last observed activity from this validator */
  lastSeenAt: ConsensusTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 3: RTWOConsensusVote
// ─────────────────────────────────────────────

export interface RTWOConsensusVote {
  /** Unique identifier for this vote */
  voteId: string | null;

  /** Hash of the block this vote is cast for */
  blockHash: ConsensusHash | null;

  /** Validator ID that cast this vote */
  validatorId: ConsensusValidatorId | null;

  /** Type of vote (prevote or precommit) */
  voteType: VoteType;

  /** Consensus round in which this vote was cast */
  round: ConsensusRound;

  /** Epoch number in which this vote was cast */
  epoch: ConsensusEpochNumber;

  /** Cryptographic signature from the voting validator */
  signature: ConsensusSignature | null;

  /** Timestamp when the vote was created */
  timestamp: ConsensusTimestamp;

  /** Current validation status of this vote */
  status: VoteStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 4: RTWOEpoch
// ─────────────────────────────────────────────

export interface RTWOEpoch {
  /** Sequential epoch number */
  epochNumber: ConsensusEpochNumber;

  /** Block height at which this epoch begins */
  startBlock: ConsensusBlockHeight;

  /** Block height at which this epoch ends */
  endBlock: ConsensusBlockHeight;

  /** Hash of the validator set active during this epoch */
  validatorSetHash: ConsensusHash | null;

  /** Current lifecycle status of this epoch */
  status: EpochStatus;

  /** Timestamp when this epoch started */
  startedAt: ConsensusTimestamp;

  /** Timestamp when this epoch was finalized (null if not yet finalized) */
  finalizedAt: ConsensusTimestamp;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 5: RTWOFinalityRecord
// ─────────────────────────────────────────────

export interface RTWOFinalityRecord {
  /** Block number that was finalized */
  blockNumber: ConsensusBlockHeight;

  /** Hash of the finalized block */
  blockHash: ConsensusHash | null;

  /** Timestamp when finality was achieved */
  finalizedAt: ConsensusTimestamp;

  /** Hash of the validator set that finalized this block */
  validatorSetHash: ConsensusHash | null;

  /** Number of valid votes that contributed to finality */
  voteCount: number | null;

  /** Current finality status */
  finalityStatus: FinalityStatus;
}

// ─────────────────────────────────────────────
// DOMAIN MODEL 6: RTWOSlashingRule
// ─────────────────────────────────────────────

export interface RTWOSlashingRule {
  /** Unique identifier for this slashing rule */
  ruleId: string;

  /** Human-readable name for this rule */
  ruleName: string;

  /** Severity level of the infraction */
  severity: SlashingSeverity;

  /** Detailed description of what triggers this rule */
  description: string;

  /** Type of penalty applied when this rule is triggered */
  penaltyType: SlashingPenaltyType;

  /** Whether this rule is currently active */
  enabled: boolean;

  /** Operational status of this rule */
  status: SlashingRuleStatus;
}

// ─────────────────────────────────────────────
// EPOCH LIFECYCLE STAGES
// ─────────────────────────────────────────────

export interface EpochLifecycleStage {
  stage: EpochStatus;
  label: string;
  description: string;
  terminal: boolean;
}

export const EPOCH_LIFECYCLE: EpochLifecycleStage[] = [
  {
    stage: 'pending',
    label: 'Pending',
    description: 'Epoch is scheduled but has not yet started. Validator set is being prepared.',
    terminal: false,
  },
  {
    stage: 'active',
    label: 'Active',
    description: 'Epoch is currently running. Validators are participating in consensus rounds.',
    terminal: false,
  },
  {
    stage: 'finalizing',
    label: 'Finalizing',
    description:
      'Epoch end block has been reached. Final rewards and slashing are being calculated.',
    terminal: false,
  },
  {
    stage: 'finalized',
    label: 'Finalized',
    description: 'Epoch has been fully finalized. Rewards distributed and validator set updated.',
    terminal: true,
  },
  {
    stage: 'failed',
    label: 'Failed',
    description: 'Epoch failed to finalize due to consensus failure or critical error.',
    terminal: true,
  },
];

// ─────────────────────────────────────────────
// BUILT-IN SLASHING RULES
// ─────────────────────────────────────────────

export const CONSENSUS_SLASHING_RULES: RTWOSlashingRule[] = [
  {
    ruleId: 'slash-001',
    ruleName: 'Double Signing',
    severity: 'critical',
    description:
      'Validator signed two different blocks at the same height and round (equivocation).',
    penaltyType: 'tombstone',
    enabled: true,
    status: 'enabled',
  },
  {
    ruleId: 'slash-002',
    ruleName: 'Downtime Violation',
    severity: 'medium',
    description:
      'Validator missed more than the allowed threshold of consensus rounds within an epoch.',
    penaltyType: 'jail',
    enabled: true,
    status: 'enabled',
  },
  {
    ruleId: 'slash-003',
    ruleName: 'Invalid Vote Submission',
    severity: 'high',
    description: 'Validator submitted a vote with an invalid or forged signature.',
    penaltyType: 'stake_reduction',
    enabled: true,
    status: 'enabled',
  },
  {
    ruleId: 'slash-004',
    ruleName: 'Proposer Misbehavior',
    severity: 'high',
    description: 'Proposer submitted an invalid or malformed block proposal.',
    penaltyType: 'jail',
    enabled: true,
    status: 'enabled',
  },
  {
    ruleId: 'slash-005',
    ruleName: 'Unauthorized Validator Activity',
    severity: 'critical',
    description: 'Validator participated in consensus without being in the active validator set.',
    penaltyType: 'removal',
    enabled: true,
    status: 'enabled',
  },
  {
    ruleId: 'slash-006',
    ruleName: 'Epoch Finality Obstruction',
    severity: 'high',
    description: 'Validator deliberately withheld votes to prevent epoch finalization.',
    penaltyType: 'stake_reduction',
    enabled: false,
    status: 'draft',
  },
];

// ─────────────────────────────────────────────
// PHASE 4 READINESS CHECKLIST
// ─────────────────────────────────────────────

export interface Phase4ChecklistItem {
  id: string;
  label: string;
  status: 'complete' | 'pending' | 'deferred';
  note: string;
}

export const PHASE4_READINESS_CHECKLIST: Phase4ChecklistItem[] = [
  {
    id: 'p4-01',
    label: 'consensus-engine module created',
    status: 'complete',
    note: 'src/lib/consensus-engine/ module exists',
  },
  {
    id: 'p4-02',
    label: 'RTWOConsensusState domain model defined',
    status: 'complete',
    note: '9 fields: height, round, epoch, phase, proposer, validatorSetHash, lockedBlockHash, finalizedBlockHash, status',
  },
  {
    id: 'p4-03',
    label: 'RTWOValidatorCandidate domain model defined',
    status: 'complete',
    note: '8 fields: validatorId, operatorAddress, publicKey, status, votingPower, stakeAmount, joinedAt, lastSeenAt',
  },
  {
    id: 'p4-04',
    label: 'RTWOConsensusVote domain model defined',
    status: 'complete',
    note: '9 fields: voteId, blockHash, validatorId, voteType, round, epoch, signature, timestamp, status',
  },
  {
    id: 'p4-05',
    label: 'RTWOEpoch domain model defined',
    status: 'complete',
    note: '7 fields: epochNumber, startBlock, endBlock, validatorSetHash, status, startedAt, finalizedAt',
  },
  {
    id: 'p4-06',
    label: 'RTWOFinalityRecord domain model defined',
    status: 'complete',
    note: '6 fields: blockNumber, blockHash, finalizedAt, validatorSetHash, voteCount, finalityStatus',
  },
  {
    id: 'p4-07',
    label: 'RTWOSlashingRule domain model defined',
    status: 'complete',
    note: '7 fields: ruleId, ruleName, severity, description, penaltyType, enabled, status',
  },
  {
    id: 'p4-08',
    label: 'Consensus phases defined (8 phases)',
    status: 'complete',
    note: 'Idle → Proposer Selection → Block Proposal → Vote Collection → Vote Validation → Finality Check → Finalized → Failed',
  },
  {
    id: 'p4-09',
    label: 'Validator statuses defined (6 statuses)',
    status: 'complete',
    note: 'Candidate, Active, Inactive, Jailed, Slashed, Removed',
  },
  {
    id: 'p4-10',
    label: 'Service interfaces defined',
    status: 'complete',
    note: '10 service interfaces with placeholder implementations',
  },
  {
    id: 'p4-11',
    label: 'Core Engine integration complete',
    status: 'complete',
    note: 'Integrated with R3 Core Orchestrator, R4 Blockchain Protocol Engine, R6 Security & Automation, R7 Enterprise Services',
  },
  {
    id: 'p4-12',
    label: 'Consensus dashboard page created',
    status: 'complete',
    note: '/consensus-engine route with full architecture visualization',
  },
  {
    id: 'p4-13',
    label: 'No fake blockchain data shown',
    status: 'complete',
    note: 'Truthful Data Policy enforced — all runtime values are null or empty states',
  },
  {
    id: 'p4-14',
    label: 'P2P runtime bridge activated',
    status: 'complete',
    note: 'Consensus Engine connected to operational P2P provider — no fabricated network state',
  },
];

// ─────────────────────────────────────────────
// CONSENSUS SERVICE IDS
// ─────────────────────────────────────────────

export const CONSENSUS_SERVICE_IDS = {
  STATE_MANAGEMENT: 'consensus-state-management',
  EPOCH_MANAGEMENT: 'consensus-epoch-management',
  VALIDATOR_REGISTRY: 'consensus-validator-registry',
  PROPOSER_SELECTION: 'consensus-proposer-selection',
  VOTE_VALIDATION: 'consensus-vote-validation',
  VOTE_AGGREGATION: 'consensus-vote-aggregation',
  FINALITY_CALCULATION: 'consensus-finality-calculation',
  SLASHING_EVALUATION: 'consensus-slashing-evaluation',
  LIFECYCLE_MANAGEMENT: 'consensus-lifecycle-management',
  EVENT_PUBLISHING: 'consensus-event-publishing',
} as const;

// ─────────────────────────────────────────────
// FACTORY FUNCTIONS (empty/null state)
// ─────────────────────────────────────────────

export function createEmptyConsensusState(): RTWOConsensusState {
  return {
    currentHeight: null,
    currentRound: null,
    currentEpoch: null,
    phase: 'idle',
    proposer: null,
    validatorSetHash: null,
    lockedBlockHash: null,
    finalizedBlockHash: null,
    status: 'not_initialized',
  };
}

export function createEmptyValidatorCandidate(): RTWOValidatorCandidate {
  return {
    validatorId: null,
    operatorAddress: null,
    publicKey: null,
    status: 'candidate',
    votingPower: null,
    stakeAmount: null,
    joinedAt: null,
    lastSeenAt: null,
  };
}

export function createEmptyConsensusVote(): RTWOConsensusVote {
  return {
    voteId: null,
    blockHash: null,
    validatorId: null,
    voteType: 'prevote',
    round: null,
    epoch: null,
    signature: null,
    timestamp: null,
    status: 'pending',
  };
}

export function createEmptyEpoch(): RTWOEpoch {
  return {
    epochNumber: null,
    startBlock: null,
    endBlock: null,
    validatorSetHash: null,
    status: 'pending',
    startedAt: null,
    finalizedAt: null,
  };
}

export function createEmptyFinalityRecord(): RTWOFinalityRecord {
  return {
    blockNumber: null,
    blockHash: null,
    finalizedAt: null,
    validatorSetHash: null,
    voteCount: null,
    finalityStatus: 'not_finalized',
  };
}
